import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useReactiveVar, useLazyQuery } from '@apollo/client';
import { Row, Dropdown, Button, Col, Tooltip, Input, Spin } from 'antd';
import PropTypes from 'prop-types';

import { getUserId } from 'graphql/cache';
import { proposalsClientVar, hubspotUrlParams } from 'graphql/cache';
import {
  FETCH_HUBSPOT_CONTACTS,
  SEARCH_HUBSPOT_CONTACTS,
} from 'graphql/queries/integrationQueries';
import * as _ from 'lodash';
import images from 'constants/images';
import AddRoundIcon from 'components/Icons/AddRoundIcon';
import AddUser from 'components/Icons/AddUser';
import InfoIcon from 'components/Icons/InfoIcon';

import './HubspotClientName.scss';

const HubspotClientName = ({
  onChange,
  clientNameValue,
  label,
  language,
  dupClientErr,
  fromTemplate,
  alert,
}) => {
  const proposalsClients = useReactiveVar(proposalsClientVar);

  const [clientName, setClientName] = useState(clientNameValue);
  const [clientNames, setClientNames] = useState(proposalsClients);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [reset, setReset] = useState(true);
  const searchInputRef = useRef(null);
  const HubspotClientNameRef = useRef(null);

  const [visibleHubspotClientName, setVisibleHubspotClientName] = useState(false);

  useEffect(() => {
    setClientName(clientNameValue);
  }, [clientNameValue]);

  useEffect(() => {
    if (hubspotUrlParams().from === 'CONTACT') {
      const firstName = hubspotUrlParams()?.firstname || '';
      const lastName = hubspotUrlParams()?.lastname || '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      if (fromTemplate) {
        onChange(
          fullName,
          hubspotUrlParams()?.email || '',
          '',
          '',
          '',
          '',
          hubspotUrlParams()?.hubspotContactId || ''
        );
      } else {
        onChange({
          type: 'setName',
          action: 'name',
          value: fullName,
        });
        onChange({ type: 'setEmail', action: 'email', value: hubspotUrlParams()?.email || '' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [fetchHubspotContacts] = useLazyQuery(FETCH_HUBSPOT_CONTACTS, {
    skip: !getUserId(),
    fetchPolicy: 'network-only',
    onCompleted: ({ fetchHubSpotContacts }) => {
      const newContacts = fetchHubSpotContacts.contacts || [];
      const newNextCursor = fetchHubSpotContacts.nextCursor;

      if (nextCursor) {
        setClientNames((prevContacts) => {
          const uniqueContacts = newContacts.filter(
            (contact) => !prevContacts.some((c) => c.id === contact.id)
          );
          return [...prevContacts, ...uniqueContacts];
        });
      } else {
        setClientNames(newContacts);
      }

      setNextCursor(newNextCursor);
      setHasMore(!!newNextCursor);
      setLoading(false);
    },
    onError: (error) => {
      console.log(error);
      setLoading(false);
    },
  });

  const [searchHubspotContacts] = useLazyQuery(SEARCH_HUBSPOT_CONTACTS, {
    skip: !getUserId() || !searchTerm?.trim(),
    fetchPolicy: 'network-only',
    onCompleted: ({ searchHubSpotContacts }) => {
      const newContacts = searchHubSpotContacts?.contacts || [];
      const newNextCursor = searchHubSpotContacts?.nextCursor;

      if (nextCursor) {
        if (reset) {
          setClientNames(newContacts);
        } else {
          setClientNames((prevContacts) => {
            const uniqueContacts = newContacts.filter(
              (contact) => !prevContacts.some((c) => c.id === contact.id)
            );
            return [...prevContacts, ...uniqueContacts];
          });
          setReset(true);
        }
      } else {
        setClientNames(newContacts);
      }

      setNextCursor(newNextCursor);
      setHasMore(!!newNextCursor);
      setLoading(false);
    },
    onError: (error) => {
      setLoading(false);
    },
  });

  // Fetch initial contacts when component mounts
  useEffect(() => {
    fetchHubspotContacts({
      variables: {
        after: null, // Start from the beginning
      },
    });
  }, []); // eslint-disable-line

  /**
   * Load more contacts for pagination
   * Only executes if not currently loading, has more data, and has a next cursor
   */
  const loadMoreContacts = useCallback(() => {
    if (loading || !hasMore || !nextCursor) return;

    setLoading(true);
    if (searchTerm) {
      setReset(false);
      searchHubspotContacts({
        variables: {
          query: searchTerm,
          after: nextCursor,
          limit: 10,
        },
      });
    } else {
      fetchHubspotContacts({
        variables: {
          after: nextCursor, // Use the cursor from previous response
        },
      });
    }
  }, [loading, hasMore, nextCursor, searchTerm, searchHubspotContacts, fetchHubspotContacts]);

  /**
   * Handle scroll events for infinite scroll pagination
   * Triggers loading more contacts when user scrolls near the bottom
   */
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Load more when user scrolls to bottom (with 50px threshold for better UX)
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        loadMoreContacts();
      }
    },
    [loadMoreContacts]
  );

  // Filter and sort contacts based on search term (supports both name and email search)
  const filteredContacts = clientNames
    .filter(
      (contact) =>
        contact?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        (contact?.email && contact?.email?.toLowerCase().includes(searchTerm?.toLowerCase()))
    )
    .sort((a, b) => {
      if (!searchTerm) return 0;

      // Prioritize exact matches over partial matches
      const aNameExact = a.name?.toLowerCase() === searchTerm?.toLowerCase();
      const bNameExact = b.name?.toLowerCase() === searchTerm?.toLowerCase();

      if (aNameExact && !bNameExact) return -1;
      if (!aNameExact && bNameExact) return 1;

      // Then prioritize name matches over email matches
      const aNameMatch = a.name?.toLowerCase().includes(searchTerm?.toLowerCase());
      const bNameMatch = b.name?.toLowerCase().includes(searchTerm?.toLowerCase());

      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;

      return 0;
    });

  // Reset selected index when search term changes to prevent invalid selections
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchTerm]);

  const debounceSearchHandler = useMemo(
    () =>
      _.debounce((value = '') => {
        setLoading(true);
        searchHubspotContacts({
          variables: {
            query: value,
            after: null,
            limit: 10,
          },
        });
      }, 1000),
    [searchHubspotContacts]
  );

  useEffect(() => {
    return () => {
      debounceSearchHandler.cancel();
    };
  }, [debounceSearchHandler]);

  /**
   * Handle contact selection or new contact creation
   * @param {string} name - The contact name
   * @param {string} email - The contact email
   */
  const onInputChange = useCallback(
    (value, email, company, hubspotContactId) => {
      setClientName(value);
      setSelectedContactId(hubspotContactId); // Set the selected contact ID
      setVisibleHubspotClientName(false);
      setSelectedIndex(-1);

      if (fromTemplate) {
        // Template mode: pass name and email for template handling
        onChange(value, email, company, '', '', '', hubspotContactId);
      } else {
        onChange({ type: 'setName', action: 'name', value: company || '' });
        onChange({ type: 'setEmail', action: 'email', value: email || '' });
        onChange({
          type: 'setHubspotContactId',
          action: 'hubspotContactId',
          value: hubspotContactId || '',
        });
        onChange({ type: 'setContact', action: 'contact', value: value || '' });
      }
    },
    [onChange, fromTemplate]
  );

  /**
   * Handle keyboard navigation within the dropdown
   * Supports arrow keys for navigation, enter for selection, and escape to close
   */
  const handleKeyDown = useCallback(
    (event) => {
      if (!visibleHubspotClientName) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          // Move selection down, but don't go beyond the last item
          setSelectedIndex((prev) => (prev < filteredContacts.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          event.preventDefault();
          // Move selection up, allow going back to -1 (no selection)
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          event.preventDefault();
          // Select the currently highlighted contact or use search text
          if (selectedIndex >= 0 && selectedIndex < filteredContacts.length) {
            const selectedContact = filteredContacts[selectedIndex];
            onInputChange(
              selectedContact?.name,
              selectedContact?.email,
              selectedContact?.company,
              selectedContact?.id
            );
          } else if (searchTerm.trim()) {
            // If no contact is selected but there's search text, use the search text
            onInputChange(searchTerm.trim(), '', '', '');
          }
          break;
        case 'Escape':
          event.preventDefault();
          // Close the dropdown and reset selection
          setVisibleHubspotClientName(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
    },
    [visibleHubspotClientName, selectedIndex, filteredContacts, searchTerm, onInputChange]
  );

  // Custom dropdown menu with search, pagination, and keyboard navigation
  const menu = (
    <div
      className={`wix-client-name-dropdown-menu hubspot-contact-dropdown ${
        language === 'hebrew' || language === 'arabic' ? 'language-rtl' : ''
      }`}
      onScroll={handleScroll} // Enable infinite scroll
      onKeyDown={handleKeyDown} // Enable keyboard navigation
      tabIndex={0} // Make the dropdown focusable for keyboard events
    >
      {/* Search input for filtering contacts */}
      <div className="search-container">
        <Input
          className="hubspot-client-name-input"
          ref={searchInputRef} // Ref for auto-focus functionality
          placeholder="Search contacts..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            debounceSearchHandler(e.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.stopPropagation();
              setVisibleHubspotClientName(false);
              onInputChange(!!event.target.value?.trim() ? event.target.value : '', '', '', '');
            }
          }}
          size="small"
          allowClear // Allow clearing the search input
          suffix={<AddUser />} // Add user icon for visual consistency
        />
      </div>

      {/* Render filtered contacts with keyboard navigation support */}
      {filteredContacts?.map((contact, index) => (
        <div
          key={contact.id}
          className={`contact-option ${contact.id === selectedContactId ? 'selected' : ''}`}
          onClick={() => onInputChange(contact.name, contact.email, contact.company, contact.id)} // Handle contact selection
        >
          <div className="contact-name">{contact.name}</div>
          {contact.email && <div className="contact-email">{contact.email}</div>}
        </div>
      ))}

      {/* Loading indicator for pagination */}
      {loading && (
        <div className="loading-container">
          <Spin size="small" /> Loading more contacts...
        </div>
      )}

      {/* Message when all contacts have been loaded */}
      {!hasMore && clientNames.length > 0 && filteredContacts.length === clientNames.length && (
        <div className="no-more-contacts">No more contacts to load</div>
      )}

      {/* Message when no contacts are found (either no data or no search results) */}
      {filteredContacts.length === 0 && !loading && (
        <div className="no-contacts-found">
          {searchTerm ? 'No contacts match your search' : 'No contacts found'}
        </div>
      )}
    </div>
  );

  return (
    <div className="wix-client-name-dropdown-container" ref={HubspotClientNameRef}>
      {/* Label and tooltip section */}
      {label && (
        <Row>
          <label className="input-label">
            {label}
            <span className="asterisk">*</span>
          </label>
          <Tooltip
            overlayClassName="wizard-tooltip-email"
            title="Search for a hubspot contact or add a new one that will be available on Prospero only. 
            Fetching existing contacts is available only from the Contacts and Leads tabs on your hubspot dashboard."
            color="black"
            key="white">
            <InfoIcon />
          </Tooltip>
        </Row>
      )}
      {alert && (
        <div className="hubspot-contacts-alert">
          Fetching existing contacts is available only from the Contacts and Leads tabs on your
          hubspot dashboard
        </div>
      )}
      <Dropdown
        overlay={menu}
        trigger={['click']}
        visible={visibleHubspotClientName}
        onVisibleChange={(visible) => {
          setVisibleHubspotClientName(visible);
          if (!visible) {
            // Reset state when dropdown closes
            setSearchTerm('');
            setSelectedIndex(-1);
          } else {
            // Reset selection when dropdown opens
            setSelectedIndex(-1);
            // Auto-focus search input when dropdown opens (with small delay for smooth UX)
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 100);
          }
        }}
        placement="bottomLeft"
        getPopupContainer={(trigger) => trigger.parentElement} // Ensure proper positioning
      >
        {/* Dropdown trigger button */}
        <Button
          id="wix-client-name-dropdown-button"
          className={`hubspot-client-name ${!clientName?.trim() ? 'error' : ''}`}
          onClick={() => setVisibleHubspotClientName(!visibleHubspotClientName)}>
          <Row justify="space-between">
            <Col>
              {/* Hubspot contact icon */}
              <img
                alt="Hubspot"
                className={fromTemplate ? 'hubspot-template' : ''}
                src={images.HUBSPOT_ICON}
              />
              <span className={`hubspot-button-text ${fromTemplate ? 'hubspot-template' : ''}`}>
                {!!clientName?.trim() ? clientName : 'Select/Add Contact (↵ to add)'}
              </span>
            </Col>
            <Col>
              {/* Add icon to indicate dropdown functionality */}
              <AddRoundIcon />
            </Col>
          </Row>
        </Button>
      </Dropdown>
      <p>{dupClientErr}</p>
    </div>
  );
};

HubspotClientName.defaultProps = {
  dupClientErr: '',
  clientNameValue: '',
};

HubspotClientName.propTypes = {
  type: PropTypes.string.isRequired,
  action: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  clientNameValue: PropTypes.string,
  label: PropTypes.string,
  dupClientErr: PropTypes.string,
};

export default HubspotClientName;
